import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { previewPages } from '../data/previews';
import { productShortName } from '../data/offer';
export function LookInsideCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = previewPages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + total) % total;
    const slide = track.children[next] as HTMLElement | undefined;
    if (!slide) return;
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let distance = Infinity;
      Array.from(track.children).forEach((child, index) => {
        const el = child as HTMLElement;
        const d = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
        if (d < distance) {
          distance = d;
          closest = index;
        }
      });
      setActive(closest);
    };
    onScroll();
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);
  return <section className="bg-background py-16 sm:py-20" aria-labelledby="inside-title" data-id="element-96" data-mp="26a2eog1jbvw9">
      <div className="mx-auto max-w-6xl px-4" data-id="element-97" data-mp="26c5bpk13upah">
        <div className="text-center" data-id="element-98" data-mp="21a6cmmceeejc">
          <p className="text-sm font-extrabold uppercase tracking-[.16em] text-accent" data-id="element-99" data-mp="21b7uparu01ak">Look Inside</p>
          <h2 id="inside-title" className="mx-auto mt-3 max-w-4xl text-balance font-display text-4xl font-bold uppercase leading-[1.02] tracking-tight text-foreground sm:text-5xl" data-id="element-100" data-mp="1wdze3emc05bf">
            Discover What&apos;s Inside <span className="text-accent" data-id="element-101" data-mp="3qo32015gj72p">{productShortName}</span>
          </h2>
          <span className="mx-auto mt-4 flex w-fit items-center gap-1" aria-hidden="true" data-id="element-102" data-mp="1ozfcexvl1vn1">
            <span className="h-1 w-10 rounded-full bg-primary" data-id="element-103" data-mp="1tt1clqkk871y" />
            <span className="h-1 w-3 rounded-full bg-accent" data-id="element-104" data-mp="1f3by6tngi6jq" />
          </span>
          <p className="mx-auto mt-6 max-w-3xl leading-relaxed text-muted" data-id="element-105" data-mp="1k1ds69mf7q9z">
            Battery technology fundamentals, lithium-ion batteries, LFP, NMC, sodium-ion and advanced battery chemistries,
            Battery Management Systems (BMS), power conversion systems, BESS architecture, battery performance, energy
            storage applications, solar and wind energy integration, microgrids, grid-connected storage, battery safety,
            thermal management, degradation, reliability, lifecycle management, recycling, sustainability, and
            professional technical references.
          </p>
        </div>

        <div className="relative mt-10" role="region" aria-roledescription="carousel" aria-label="Sample pages" data-id="element-106" data-mp="rd84be02ll5x">
          <div ref={trackRef} className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-[10%] pb-2 [scrollbar-width:none] sm:px-[20%] md:px-[35%] [&::-webkit-scrollbar]:hidden" data-id="element-107" data-mp="js5n4257sw7a">
            {previewPages.map((page, index) => <figure key={page.src} className={`w-[80%] shrink-0 snap-center overflow-hidden rounded-2xl border bg-surface shadow-lg shadow-black/40 transition-[border-color,opacity] duration-200 ease-out sm:w-[60%] md:w-[30%] ${index === active ? 'border-primary opacity-100' : 'border-primary/30 opacity-70'}`} aria-roledescription="slide" aria-label={`${index + 1} of ${total}`} data-id="element-108" data-mp="tpk1zxbhpcxh" data-mpi={index}>
                <img src={page.src} alt={page.alt} loading="lazy" className="block aspect-[4/5] w-full object-cover" data-id="element-109" data-mp="au3i7bid1jeq" />
              </figure>)}
          </div>

          <button type="button" onClick={() => goTo(active - 1)} className="absolute left-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-primary bg-surface text-foreground shadow-lg shadow-black/40 transition-colors duration-150 ease-out hover:bg-surface-hover focus:outline-none focus-visible:ring-4 focus-visible:ring-accent/50 sm:-left-2" aria-label="Previous page" data-id="element-110" data-mp="3eh2fp3gkeenm">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-111" data-mp="2lrahd5szsw8f" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} className="absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-primary bg-surface text-foreground shadow-lg shadow-black/40 transition-colors duration-150 ease-out hover:bg-surface-hover focus:outline-none focus-visible:ring-4 focus-visible:ring-accent/50 sm:-right-2" aria-label="Next page" data-id="element-112" data-mp="2qju4atdkl3pw">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-113" data-mp="39h4rvgpua8pi" />
          </button>
        </div>

        <div className="mt-6 flex justify-center gap-2" data-id="element-114" data-mp="1a63oipd38r0f">
          {previewPages.map((page, index) => <button key={page.src} type="button" onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${index === active ? 'w-6 bg-accent' : 'w-2 bg-primary/50 hover:bg-primary'}`} aria-label={`Go to page ${index + 1}`} aria-current={index === active} data-id="element-115" data-mp="1f45hq348a6pm" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}